<script lang="tsx">
  import {
    PropType, defineComponent,
    ref, onBeforeUnmount,
    Teleport,
    Transition,
  } from 'vue'
  import {
    disableBodyScroll,
    enableBodyScroll,
    clearAllBodyScrollLocks,
  } from 'body-scroll-lock'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { StackingOrder } from '@xhs/yam-beer/cmp/constants'
  import { BeerStyleObj } from '../../../core'
  import initStyle from './style'

  initStyle()

  /**
   * Modal 的通用逻辑组件，可以在这个组件上扩展自定义 Modal 样式组件
   *
   * 扩展组件：
   * @see BeerModal #sfc-BeerModal
   * @see BeerPreviewModal #sfc-BeerPreviewModal
   * @see BeerQrPreviewModal #sfc-BeerQrPreviewModal
   */
  export default defineComponent({
    name: 'BeerModalBase',
    inheritAttrs: false,
    props: {
      /**
       * 遮层 BeerStyle
       */
      maskBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * zIndex 的值
       */
      stack: {
        type: Number,
        default: StackingOrder.MODAL,
      },
      /**
       * 同步属性
       * v-model:show="modalShow"
       */
      show: {
        type: Boolean,
        default: false,
      },
      /**
       * 使用 fixed
       *
       * @deprecated 4.1.0
       */
      isPortal: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否添加到body，一般嵌套modal时用
       *
       * @version 4.1.0
       */
      fixed: {
        type: Boolean,
        default: false,
      },
      /**
       * modal关闭时调用,
       * 同时提供@close=“dealClose”事件
       */
      handleClose: {
        type: Function,
        default: () => () => ({}),
      },
      /**
       * 点击内置的确认按钮时调用,
       * 同时提供@confirm=“dealConfirm”事件
       */
      handleConfirm: {
        type: Function,
        default: () => () => ({}),
      },
      /**
       * 点击背景蒙层是否触发关闭
       */
      hideWhenClickOutside: {
        type: Boolean,
        default: false,
      },
      /**
       * fixed 模式下 Teleport 挂载的对象
       */
      portTarget: {
        type: [String, Object],
        default: 'body',
      },
    },
    emits: {
      close: null,
      /**
       * v-model:show 的支持，child 可以告诉 parent 修改 props <=> :show @update:show
       *
       * @arg {bool} show - 更新的值
       * @arg {bool} show - 更新的值
       */
      'update:show': null,
      /**
       * 确认的事件，和 handleConfirm 是等价的，看你取向
       */
      confirm: null,
    },
    setup(props, ctx) {
      const mask = ref<{
        $el: HTMLElement | Element
      }>()

      const onEnter = () => {
        if (mask.value) {
          disableBodyScroll(mask.value.$el)
        }
      }

      const onLeave = () => {
        if (mask.value) {
          enableBodyScroll(mask.value.$el)
        } else {
          // 一般不会遇到这种情况，清空所有 scroll lock just in case
          clearAllBodyScrollLocks()
        }
      }

      const hide = () => ctx.emit('update:show', false)

      const dealClose = () => {
        hide()
        ctx.emit('close')
        props.handleClose()
      }

      const dealConfirm = () => {
        ctx.emit('confirm', { close: hide })
        props.handleConfirm({ close: hide })
      }

      const handleClickMask = (event: Event) => {
        if (props.hideWhenClickOutside && event.target === mask.value?.$el) {
          dealClose()
        }
      }

      onBeforeUnmount(() => {
        if (mask.value) {
          enableBodyScroll(mask.value.$el)
        }
      })

      // FIXME: jsx - animation - v-if not playing nicely
      // checkout https://github.com/vuejs/jsx-next/issues/193
      // after this issue resolved, we won't need to render stack component if none shown state
      return () => {
        const Cmp = (
          <Transition
            appear
            onEnter={onEnter}
            onLeave={onLeave}
            duration={240}
          >
            {props.show && (
              <div class="modal-beer-box" {...ctx.attrs}>
                <NewBsBox
                  ref={mask}
                  class="modal-mask"
                  bs={{
                    zIndex: props.stack,
                    'cmp.modal': 'mask',
                    ...props.maskBs,
                  }}
                  context={props}
                  // @ts-ignore
                  onClick={handleClickMask}
                >
                  {ctx.slots?.default?.({
                    close: dealClose,
                    confirm: dealConfirm,
                  })}
                </NewBsBox>
              </div>
            )}
          </Transition>
        )
        const isInline = !props.fixed && !props.isPortal
        if (isInline) {
          return Cmp
        }
        return (
          <Teleport
            disabled={isInline}
            to={props.portTarget}>
            {Cmp}
          </Teleport>
        )
      }
    },
  })
</script>
